import axios from 'axios';

const API_URL = 'https://api.changenow.io/v1';
const API_KEY = process.env.REACT_APP_CHANGENOW_API_KEY;

if (!API_KEY) {
  throw new Error('REACT_APP_CHANGENOW_API_KEY is not defined in environment variables');
}

// Add axios instance with proper configuration
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Add request interceptor for debugging
apiClient.interceptors.request.use(request => {
  console.log('API Request:', request.method?.toUpperCase(), request.url, request.params || request.data);
  return request;
});

// Add response interceptor for debugging
apiClient.interceptors.response.use(
  response => {
    console.log('API Response:', response.status, response.data);
    return response;
  },
  error => {
    console.error('API Error:', error.message);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    }
    return Promise.reject(error);
  }
);

// Types
export interface Currency {
  ticker: string;
  name: string;
  image?: string;
  hasExternalId?: boolean;
  isExtraIdSupported?: boolean; // Added missing field from API docs
  isFiat?: boolean;
  featured?: boolean;
  isStable?: boolean;
  supportsFixedRate?: boolean;
}

export interface ApiError {
  error: string;
  message: string;
}

export interface ExchangeAmount {
  estimatedAmount: number;
  transactionSpeedForecast?: string;
  warningMessage?: string;
}

export interface MinimalExchangeAmount {
  minAmount: number;
}

export interface ExchangeRange {
  minAmount: number;
  maxAmount: number;
}

export interface MarketInfo {
  rate: number;
  minerFee: number;
  maxAmount: number | null;
  minAmount: number;
}

export interface AddressValidation {
  result: boolean;
  message?: string;
}

export interface Transaction {
  id: string;
  status: string;
  fromCurrency: string;
  toCurrency: string;
  payinAddress: string;
  payoutAddress: string;
  payinExtraId?: string;
  payoutExtraId?: string;
  refundAddress?: string;
  refundExtraId?: string;
  amount: number;
  expectedAmount?: number;
  createdAt: string;
  updatedAt?: string;
  payinHash?: string;
  payoutHash?: string;
  payinExtra?: any;
}

// API Service
// Add new interface for fixed-rate range
export interface FixedRateRange {
  min: number;
  max: number;
  rate: number;
}

// API service methods
const apiService = {
  // Get available currencies
  async getAvailableCurrencies(active = true, fixedRate = false): Promise<Currency[]> {
    try {
      const response = await apiClient.get(`/currencies`, {
        params: {
          active: active,
          fixedRate: fixedRate
        }
      });
      return response.data as Currency[];
    } catch (error) {
      console.error('Error fetching currencies:', error);
      throw error;
    }
  },

  // Get available pairs for a currency
  async getAvailablePairs(fromCurrency: string, fixedRate = false): Promise<Currency[]> {
    try {
      const response = await apiClient.get(`/currencies-to/${fromCurrency}`, {
        params: {
          fixedRate: fixedRate
        }
      });
      return response.data as Currency[];
    } catch (error) {
      console.error('Error fetching available pairs:', error);
      throw error;
    }
  },

  // Get minimal exchange amount
  async getMinimalExchangeAmount(fromCurrency: string, toCurrency: string): Promise<MinimalExchangeAmount> {
    try {
      const response = await apiClient.get(`/min-amount/${fromCurrency}_${toCurrency}`);
      return response.data as MinimalExchangeAmount;
    } catch (error) {
      console.error('Error fetching minimal amount:', error);
      throw error;
    }
  },

  // Get fixed rate exchange range
  // Get fixed rate range
  async getFixedRateRange(fromCurrency: string, toCurrency: string): Promise<ExchangeRange> {
    try {
      console.log(`Requesting fixed rate range for ${fromCurrency}_${toCurrency}`);
      // Fixed the API endpoint format according to Postman collection
      // The API key should be passed as a query parameter, not as part of the URL path
      const response = await apiClient.get(`/exchange-range/fixed-rate/${fromCurrency}_${toCurrency}`, {
        params: {
          api_key: API_KEY
        }
      });
      
      const data = response.data as any;
      
      // Log the actual response for debugging
      console.log('Fixed rate range response:', data);
      
      if (data && typeof data.minAmount === 'number' && typeof data.maxAmount === 'number') {
        return {
          minAmount: data.minAmount,
          maxAmount: data.maxAmount
        };
      } else if (data && typeof data.min === 'number' && typeof data.max === 'number') {
        return {
          minAmount: data.min,
          maxAmount: data.max
        };
      } else {
        console.error('Unexpected fixed rate range response format:', data);
        throw new Error('Invalid response format from API');
      }
    } catch (error: any) {
      console.error('Error fetching fixed rate range:', error);
      
      // Improved error handling with more details
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        
        // Check if the API returned a specific error message
        if (error.response.data && error.response.data.error) {
          // Check specifically for fixed_rate_not_enabled error
          if (error.response.data.error === 'fixed_rate_not_enabled') {
            throw new Error('fixed_rate_not_enabled');
          } else {
            throw new Error(`API Error: ${error.response.data.error}`);
          }
        }
      }
      
      throw error;
    }
  },

  // Get estimated exchange amount (standard flow)
  async getEstimatedExchangeAmount(fromCurrency: string, toCurrency: string, amount: number): Promise<ExchangeAmount> {
    try {
      const response = await apiClient.get(`/exchange-amount/${amount}/${fromCurrency}_${toCurrency}`);
      return response.data as ExchangeAmount;
    } catch (error) {
      console.error('Error fetching estimated amount:', error);
      throw error;
    }
  },

  // Get estimated fixed rate exchange amount
  async getEstimatedFixedRateAmount(fromCurrency: string, toCurrency: string, amount: number): Promise<ExchangeAmount> {
    try {
      // Fixed the API endpoint format according to Postman collection
      // The API key should be passed as a query parameter, not as part of the URL path
      const response = await apiClient.get(`/exchange-amount/fixed-rate/${amount}/${fromCurrency}_${toCurrency}`, {
        params: {
          api_key: API_KEY
        }
      });
      return response.data as ExchangeAmount;
    } catch (error) {
      console.error('Error fetching fixed rate estimated amount:', error);
      throw error;
    }
  },

  // Create standard exchange transaction
  async createExchangeTransaction(
    fromCurrency: string,
    toCurrency: string,
    amount: number,
    address: string,
    extraId?: string,
    refundAddress?: string,
    refundExtraId?: string
  ): Promise<Transaction> {
    try {
      // Make sure we're only including non-empty values
      const payload: any = {
        from: fromCurrency,
        to: toCurrency,
        amount: amount.toString(), // API might expect string values
        address: address
      };
      
      if (extraId) payload.extraId = extraId;
      if (refundAddress) payload.refundAddress = refundAddress;
      if (refundExtraId) payload.refundExtraId = refundExtraId;
      
      console.log('Transaction payload:', payload);
      
      const response = await apiClient.post(`/transactions/${API_KEY}`, payload);
      return response.data as Transaction;
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  },

  // Create fixed rate exchange transaction
  async createFixedRateTransaction(
    fromCurrency: string,
    toCurrency: string,
    amount: number,
    address: string,
    extraId?: string,
    refundAddress?: string,
    refundExtraId?: string
  ): Promise<Transaction> {
    try {
      // Make sure we're only including non-empty values
      const payload: any = {
        from: fromCurrency,
        to: toCurrency,
        amount: amount.toString(), // API might expect string values
        address: address
      };
      
      if (extraId) payload.extraId = extraId;
      if (refundAddress) payload.refundAddress = refundAddress;
      if (refundExtraId) payload.refundExtraId = refundExtraId;
      
      console.log('Fixed rate transaction payload:', payload);
      
      const response = await apiClient.post(`/transactions/fixed-rate`, payload, {
        params: {
          api_key: API_KEY
        }
      });
      return response.data as Transaction;
    } catch (error) {
      console.error('Error creating fixed rate transaction:', error);
      throw error;
    }
  },

  // Get transaction status
  async getTransactionStatus(id: string): Promise<Transaction> {
    try {
      const response = await apiClient.get(`/transactions/${id}/${API_KEY}`);
      return response.data as Transaction;
    } catch (error) {
      console.error('Error fetching transaction status:', error);
      throw error;
    }
  }
};

export default apiService;